import { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Pen, Type, Eraser, Loader2 } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const SignupModal = ({ isOpen, onClose, selectedPackage }) => {
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureType, setSignatureType] = useState("draw");
  const [typedSignature, setTypedSignature] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [agreedTerms, setAgreedTerms] = useState(false);
  
  const [formData, setFormData] = useState({
    company_name: "",
    kvk_number: "",
    contact_name: "",
    email: "",
    phone: "",
    address: "",
    postal_code: "",
    city: ""
  });

  // Initialize canvas
  useEffect(() => {
    if (isOpen && canvasRef.current && signatureType === "draw") {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      ctx.strokeStyle = "#C9A227";
      ctx.lineWidth = 2;
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
    }
  }, [isOpen, signatureType]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const startDrawing = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext("2d");
    
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.beginPath();
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext("2d");
    
    const x = (e.clientX || e.touches?.[0]?.clientX) - rect.left;
    const y = (e.clientY || e.touches?.[0]?.clientY) - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
  };

  const getSignatureData = () => {
    if (signatureType === "type") {
      // Create canvas from typed signature
      const canvas = document.createElement("canvas");
      canvas.width = 400;
      canvas.height = 150;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = "italic 32px 'Dancing Script', cursive, serif";
      ctx.fillStyle = "#C9A227";
      ctx.textAlign = "center";
      ctx.textBaseline = "middle";
      ctx.fillText(typedSignature, canvas.width / 2, canvas.height / 2);
      return canvas.toDataURL("image/png");
    } else {
      return canvasRef.current?.toDataURL("image/png") || "";
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!agreedTerms) {
      toast.error("U moet akkoord gaan met de voorwaarden");
      return;
    }

    const signatureData = getSignatureData();
    if (!signatureData || (signatureType === "type" && !typedSignature)) {
      toast.error("Handtekening is verplicht");
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${BACKEND_URL}/api/subscriptions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ...formData,
          package: selectedPackage?.id,
          signature_data: signatureData,
          agreed_terms: agreedTerms
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Er is iets misgegaan");
      }

      const result = await response.json();
      toast.success("Aanmelding succesvol verzonden!");
      onClose();
      navigate("/bevestiging", { state: { subscription: result } });
    } catch (error) {
      toast.error(error.message || "Er is iets misgegaan. Probeer het opnieuw.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!selectedPackage) return null;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto bg-white border-2 border-[#C9A227]" data-testid="signup-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900">
            Bevestig uw Abonnement
          </DialogTitle>
          <DialogDescription className="text-gray-600">
            Vul uw bedrijfsgegevens in en onderteken om het {selectedPackage.name} pakket af te nemen.
          </DialogDescription>
        </DialogHeader>

        {/* Package Summary */}
        <div className="bg-[#C9A227]/5 border-2 border-[#C9A227] rounded-xl p-4 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <div className="font-semibold text-gray-900">{selectedPackage.name}</div>
              <div className="text-sm text-gray-500">Maandelijks abonnement</div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-[#C9A227]">€{selectedPackage.price}</div>
              <div className="text-sm text-gray-500">{selectedPackage.period}</div>
            </div>
          </div>
          {selectedPackage.setup_fee && (
            <div className="mt-2 pt-2 border-t border-[#C9A227]/30 text-sm text-gray-600">
              + €{selectedPackage.setup_fee} eenmalige setup & branding
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Company Details */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900">Bedrijfsgegevens</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="company_name" className="text-gray-700">Bedrijfsnaam *</Label>
                <Input
                  id="company_name"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleInputChange}
                  required
                  placeholder="Uw Vloerbedrijf B.V."
                  className="border-2 border-gray-200 focus:border-[#C9A227] focus:ring-[#C9A227]"
                  data-testid="input-company-name"
                />
              </div>
              <div>
                <Label htmlFor="kvk_number" className="text-gray-700">KVK Nummer *</Label>
                <Input
                  id="kvk_number"
                  name="kvk_number"
                  value={formData.kvk_number}
                  onChange={handleInputChange}
                  required
                  placeholder="12345678"
                  className="border-2 border-gray-200 focus:border-[#C9A227] focus:ring-[#C9A227]"
                  data-testid="input-kvk"
                />
              </div>
              <div>
                <Label htmlFor="contact_name" className="text-gray-700">Contactpersoon *</Label>
                <Input
                  id="contact_name"
                  name="contact_name"
                  value={formData.contact_name}
                  onChange={handleInputChange}
                  required
                  placeholder="Jan Jansen"
                  className="border-2 border-gray-200 focus:border-[#C9A227] focus:ring-[#C9A227]"
                  data-testid="input-contact-name"
                />
              </div>
              <div>
                <Label htmlFor="email" className="text-gray-700">E-mailadres *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  placeholder="info@uwbedrijf.nl"
                  className="border-2 border-gray-200 focus:border-[#C9A227] focus:ring-[#C9A227]"
                  data-testid="input-email"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="text-gray-700">Telefoonnummer *</Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  placeholder="06 12345678"
                  className="border-2 border-gray-200 focus:border-[#C9A227] focus:ring-[#C9A227]"
                  data-testid="input-phone"
                />
              </div>
              <div>
                <Label htmlFor="address" className="text-gray-700">Adres *</Label>
                <Input
                  id="address"
                  name="address"
                  value={formData.address}
                  onChange={handleInputChange}
                  required
                  placeholder="Hoofdstraat 1"
                  className="border-2 border-gray-200 focus:border-[#C9A227] focus:ring-[#C9A227]"
                  data-testid="input-address"
                />
              </div>
              <div>
                <Label htmlFor="postal_code" className="text-gray-700">Postcode *</Label>
                <Input
                  id="postal_code"
                  name="postal_code"
                  value={formData.postal_code}
                  onChange={handleInputChange}
                  required
                  placeholder="1234 AB"
                  className="border-2 border-gray-200 focus:border-[#C9A227] focus:ring-[#C9A227]"
                  data-testid="input-postal"
                />
              </div>
              <div>
                <Label htmlFor="city" className="text-gray-700">Plaats *</Label>
                <Input
                  id="city"
                  name="city"
                  value={formData.city}
                  onChange={handleInputChange}
                  required
                  placeholder="Amsterdam"
                  className="border-2 border-gray-200 focus:border-[#C9A227] focus:ring-[#C9A227]"
                  data-testid="input-city"
                />
              </div>
            </div>
          </div>

          {/* Signature Section */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900">Digitale Handtekening</h3>
            <Tabs value={signatureType} onValueChange={setSignatureType}>
              <TabsList className="grid w-full grid-cols-2 bg-gray-100">
                <TabsTrigger value="draw" className="gap-2 data-[state=active]:bg-[#C9A227] data-[state=active]:text-white" data-testid="tab-draw">
                  <Pen className="w-4 h-4" /> Tekenen
                </TabsTrigger>
                <TabsTrigger value="type" className="gap-2 data-[state=active]:bg-[#C9A227] data-[state=active]:text-white" data-testid="tab-type">
                  <Type className="w-4 h-4" /> Typen
                </TabsTrigger>
              </TabsList>
              <TabsContent value="draw" className="mt-4">
                <div className="relative">
                  <canvas
                    ref={canvasRef}
                    width={500}
                    height={150}
                    className="w-full signature-canvas bg-gray-50 rounded-lg"
                    onMouseDown={startDrawing}
                    onMouseMove={draw}
                    onMouseUp={stopDrawing}
                    onMouseLeave={stopDrawing}
                    onTouchStart={startDrawing}
                    onTouchMove={draw}
                    onTouchEnd={stopDrawing}
                    data-testid="signature-canvas"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={clearCanvas}
                    className="absolute top-2 right-2 text-gray-500 hover:text-[#C9A227]"
                    data-testid="clear-signature-btn"
                  >
                    <Eraser className="w-4 h-4 mr-1" /> Wissen
                  </Button>
                </div>
                <p className="text-sm text-gray-500 mt-2">
                  Teken uw handtekening in het vak hierboven
                </p>
              </TabsContent>
              <TabsContent value="type" className="mt-4">
                <Input
                  value={typedSignature}
                  onChange={(e) => setTypedSignature(e.target.value)}
                  placeholder="Typ uw naam als handtekening"
                  className="text-2xl h-[150px] font-serif italic text-center border-2 border-dashed border-[#C9A227] text-[#C9A227] bg-gray-50"
                  data-testid="typed-signature-input"
                />
                <p className="text-sm text-gray-500 mt-2">
                  Door uw naam te typen gaat u akkoord met een digitale handtekening
                </p>
              </TabsContent>
            </Tabs>
          </div>

          {/* Terms */}
          <div className="flex items-start gap-3 bg-gray-50 p-4 rounded-xl border-2 border-gray-200">
            <Checkbox
              id="terms"
              checked={agreedTerms}
              onCheckedChange={setAgreedTerms}
              className="border-2 border-gray-300 data-[state=checked]:bg-[#C9A227] data-[state=checked]:border-[#C9A227]"
              data-testid="terms-checkbox"
            />
            <Label htmlFor="terms" className="text-sm text-gray-600 leading-relaxed cursor-pointer">
              Hierbij ga ik akkoord met het afnemen van het {selectedPackage.name} pakket voor €{selectedPackage.price} per maand
              {selectedPackage.setup_fee && ` (+ €${selectedPackage.setup_fee} eenmalige setup)`}. 
              Facturatie start na oplevering van de app. Ik begrijp dat dit een doorlopend abonnement betreft 
              dat maandelijks wordt gefactureerd.
            </Label>
          </div>

          {/* Actions */}
          <div className="flex gap-4 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1 py-6 border-2 border-gray-300 text-gray-700 hover:border-[#C9A227] hover:text-[#C9A227]"
              disabled={isSubmitting}
              data-testid="cancel-btn"
            >
              Annuleren
            </Button>
            <Button
              type="submit"
              className="flex-1 py-6 bg-[#C9A227] border-2 border-[#C9A227] text-white hover:bg-[#B8922A] hover:border-[#B8922A] font-semibold"
              disabled={isSubmitting}
              data-testid="submit-btn"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Verzenden...
                </>
              ) : (
                "Ondertekenen & Aanmelden"
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default SignupModal;
